import type { Enums } from "@how/database";
import { semanticRoles, type SemanticColor } from "./semantic";

/**
 * Domain-status colors, defined once and consumed by badges, tables, charts, and detail views alike.
 * A status is never colored differently on two screens (docs/UI_STANDARDS.md).
 */
export const statusColors = {
  tenant_status: {
    provisioning: semanticRoles.info,
    active: semanticRoles.success,
    suspended: semanticRoles.warning,
    offboarding: semanticRoles.warning,
    deleted: semanticRoles.neutral,
  },
  membership_status: {
    invited: semanticRoles.info,
    active: semanticRoles.success,
    suspended: semanticRoles.warning,
    removed: semanticRoles.neutral,
  },
  domain_status: {
    pending_verification: semanticRoles.info,
    verifying: semanticRoles.warning,
    active: semanticRoles.success,
    failed: semanticRoles.danger,
    disabled: semanticRoles.neutral,
  },
  entitlement_status: {
    trial: semanticRoles.info,
    active: semanticRoles.success,
    suspended: semanticRoles.warning,
    canceled: semanticRoles.neutral,
  },
  support_session_status: {
    requested: semanticRoles.info,
    active: semanticRoles.success,
    ended: semanticRoles.neutral,
    expired: semanticRoles.neutral,
    revoked: semanticRoles.danger,
  },
  module_status: {
    planned: semanticRoles.neutral,
    beta: semanticRoles.info,
    active: semanticRoles.success,
    deprecated: semanticRoles.warning,
    retired: semanticRoles.neutral,
  },
  /** A website page: never published, live and unchanged, or live with a draft that differs (ADR 0031). */
  page_status: {
    draft: semanticRoles.neutral,
    published: semanticRoles.success,
    changed: semanticRoles.warning,
  },
  /** A webhook ledger row (§13.4): waiting, done, deliberately set aside, or failed (issue #256). */
  webhook_event_status: {
    received: semanticRoles.info,
    processed: semanticRoles.success,
    ignored: semanticRoles.warning,
    // Failed is tried again on its own (ADR 0060); dead is the one a person has to look at.
    failed: semanticRoles.warning,
    dead: semanticRoles.danger,
  },
} as const satisfies {
  tenant_status: Record<Enums<"tenant_status">, SemanticColor>;
  membership_status: Record<Enums<"membership_status">, SemanticColor>;
  domain_status: Record<Enums<"domain_status">, SemanticColor>;
  entitlement_status: Record<Enums<"entitlement_status">, SemanticColor>;
  page_status: Record<"draft" | "published" | "changed", SemanticColor>;
  support_session_status: Record<Enums<"support_session_status">, SemanticColor>;
  module_status: Record<Enums<"module_status">, SemanticColor>;
  webhook_event_status: Record<Enums<"webhook_event_status">, SemanticColor>;
};
export type StatusDomain = keyof typeof statusColors;

export function statusColor<D extends StatusDomain>(
  domain: D,
  value: keyof (typeof statusColors)[D],
): SemanticColor {
  return statusColors[domain][value] as SemanticColor;
}
